<template>
  <div ref="templateTypeListRef" class="template-type-list__root">
    <div :class="['template-type-list__main', isOpenStatus && 'all']">
      <div
        v-for="item in visibleTypeList"
        :key="item.id"
        :class="['type-item', selectedTypeId === item.id && 'selected']"
        @click="onTypeClick(item)"
      >
        {{ item.name }}
      </div>
    </div>
    <div v-if="isShowMore" class="show-more" @click="onToggleClick">
      {{ isOpenStatus ? '收起' : '展开'
      }}<IconMyIconTemplateTypeDown :class="['icon-down', isOpenStatus && 'up']"></IconMyIconTemplateTypeDown>
    </div>
    <!-- <template-type-more
      v-if="isShowTemplateMore"
      ref="templateTypeMoreRef"
      :typeList="typeList"
      :selectedTypeId="selectedTypeId"
      :showedTypeList="showedTypeList"
      @on-template-item-selected="onTemplateItemSelected"
    ></template-type-more> -->
  </div>
</template>

<script setup lang="ts">
import { RespCategory } from '@vas2t-lowcode/api';
import { useResizeObserver, useThrottleFn } from '@vueuse/core';
import { computed, nextTick, onBeforeUnmount, onMounted, PropType, ref, watch } from 'vue';
import { setSelectedTemplate } from '../app-list/app-list.tool';
import { dwOnShowMore, dwOnShowMoreClick, dwOnTemplateTypeClick } from './template-type-dw';
import { getTemplateTypeEmitter } from './template-type.tools';
// import templateTypeMore from './template-type-more.vue';

const props = defineProps({
  typeList: {
    type: Array as PropType<RespCategory[]>,
    default() {
      return [];
    },
  },
});

// #region 计算一行能放置多少个
/** 模板类型宽度 */
const templateTypeWidth = ref(80);
/** 模板类型间隙宽度 */
const templateTypeSpaceWidth = ref(12);
/** 一行能放置的模板类型数量 */
const itemSizeInRow = ref(0);

/**
 * 根据当前宽度计算可放置的数量
 */
const updateItemSizeInRow = (isShowMore = false) => {
  const containerEl = templateTypeListRef.value;
  if (!containerEl) {
    return;
  }
  // 容器宽度
  const innerWidth = parseInt(getComputedStyle(containerEl).width);
  // 更新一行能放置多少个元素
  itemSizeInRow.value = Math.floor(
    (innerWidth + templateTypeSpaceWidth.value - (isShowMore ? 58 : 0)) /
      (templateTypeWidth.value + templateTypeSpaceWidth.value)
  );
};

/** 是否显示更多按钮 */
const isShowMore = computed(() => {
  return (props.typeList?.length || 0) > itemSizeInRow.value * 2;
});

watch(
  () => isShowMore.value,
  (val) => {
    if (val) {
      dwOnShowMore();
    }
  },
  { immediate: true }
);

/** 当前是展开状态还是折叠状态 */
const isOpenStatus = ref(false);

/**
 * 更新可展示的模板分类
 */
const updateVisibleTypeList = () => {
  // 更新可放置的数量
  updateItemSizeInRow(isShowMore.value);
  // 更新可视的模板类型
  if (!isShowMore.value || isOpenStatus.value) {
    visibleTypeList.value = props.typeList;
  } else {
    // 折叠状态
    visibleTypeList.value = props.typeList.slice(0, itemSizeInRow.value * 2);
  }
};

/**
 * 展开
 */
const onToggleClick = () => {
  if (!isOpenStatus.value) {
    dwOnShowMoreClick();
  }

  isOpenStatus.value = !isOpenStatus.value;
  updateVisibleTypeList();
};
// #endregion

// const isShowTemplateMore = computed(() => {
//   return props.typeList?.length > 5;
// });

// /** 其他类型 */
// const templateTypeMore = defineAsyncComponent(() => import('./template-type-more.vue'));

const selectedTypeId = ref(0);

const visibleTypeList = ref<RespCategory[]>([]);

const templateTypeListRef = ref<HTMLElement>();

// const templateTypeMoreRef = ref();
// /** 自动计算可放置的模板类型dom */
// const updateTypeList = () => {
//   const containerEl = templateTypeListRef.value;
//   if (!containerEl) {
//     return;
//   }
//   const innerWidth = parseInt(getComputedStyle(containerEl).width);

//   // TODO: 改为动态计算元素宽度
//   const singleTypeWidth = 92;
//   const moreWidth = 98;
//   const firstTypeWidth = 80;
//   // +1是解决忽略了第一个问题
//   const num = Math.floor((innerWidth - firstTypeWidth - moreWidth) / singleTypeWidth) + 1;

//   showedTypeList.value = props.typeList.slice(0, num > 5 ? 5 : num);
// };

/** 类型点击事件 */
const onTypeClick = (data: RespCategory) => {
  const id = data.id || 0;
  if (!id) {
    return;
  }

  selectedTypeId.value = id;
  setSelectedTemplate(data);
  getTemplateTypeEmitter().emit('on-template-type-click', data);

  dwOnTemplateTypeClick(data.name);
};

const onTemplateItemSelected = (data: RespCategory) => {
  if (!data) {
    return;
  }

  const id = data?.id || 0;
  selectedTypeId.value = id;
  setSelectedTemplate(data);
  getTemplateTypeEmitter().emit('on-template-type-click', data);
};

const onBindEvent = () => {
  // 通过关键字搜索
  getTemplateTypeEmitter().on('on-app-search-by-key', (key: string) => {
    // 重置选中模板类型
    setSelectedTemplate();

    const val = (key || '').trim();
    if (val) {
      // 设置选中类型为空
      selectedTypeId.value = 0;
    } else {
      // 空关键字则默认选中第一项类型
      setDefaultSelected();
    }
  });
};

/** 设置默认选中项 */
const setDefaultSelected = () => {
  const data = props.typeList[0];
  const id = data.id || 0;

  if (id) {
    selectedTypeId.value = id;

    // 通知外部当前选中项
    setSelectedTemplate(data);
    getTemplateTypeEmitter().emit('on-template-type-click', data);
  }
};

let resizeOb: ReturnType<typeof useResizeObserver>;

onMounted(() => {
  onBindEvent();
  setDefaultSelected();

  // 监测容器大小变化
  nextTick(() => {
    resizeOb = useResizeObserver(
      templateTypeListRef.value,
      useThrottleFn(() => {
        // updateTypeList();
        updateItemSizeInRow();
        updateVisibleTypeList();
      }, 500)
    );
  });
});

onBeforeUnmount(() => {
  resizeOb?.stop();
});
</script>

<style lang="scss" scoped>
.template-type-list {
  &__root {
    box-sizing: border-box;
    .show-more {
      display: inline-flex;
      align-items: center;
      cursor: pointer;
      flex: none;
      width: 58px;
      height: 22px;
      font-size: 12px;
      color: var(--lowcode-theme-common-01);
      line-height: 22px;

      &:hover {
        color: var(--lowcode-theme-common-02);
      }

      &:active {
        color: var(--lowcode-theme-wps-03);
      }

      .icon-down {
        margin-left: 4px;

        &.up {
          transform: rotate(180deg);
          transform-origin: center;
        }
      }
    }
  }

  &__main {
    display: grid;
    grid-template-rows: 24px;
    grid-template-columns: repeat(auto-fill, 102px);
    row-gap: 14px;
    column-gap: 12px;
    flex: 1;
    align-items: center;
    overflow: hidden;
    max-height: 62px;

    // 全部展开状态不限制最大高度，解决窗口拖动会主线高度闪烁
    &.all {
      max-height: none;
    }

    .type-item {
      padding: 0 10px;
      box-sizing: border-box;
      display: inline-block;
      flex: none;
      // width: 98px;
      height: 100%;
      // background: var(--lowcode-background-01);
      border-radius: 12px;
      line-height: 24px;
      font-size: 12px;
      color: var(--lowcode-custom-text-01);
      text-align: center;
      cursor: pointer;
      @extend %lowcode-single-line;

      // &:not(:first-of-type) {
      //   margin-left: 12px;
      // }

      &:hover {
        color: var(--lowcode-text-02);
        background-color: var(--lowcode-status-02);
      }

      &:active {
        color: var(--lowcode-text-03);
        background-color: var(--lowcode-status-03);
      }

      &.selected {
        background: var(--lowcode-theme-common-01);
        color: var(--lowcode-texts-03);

        &:hover {
          background: var(--lowcode-theme-common-02);
        }

        &:active {
          background: var(--lowcode-theme-common-03);
        }
      }
    }
  }
}
</style>
